import { Formik } from 'formik';
import { getInitialValues, policyHolders, insurancePlanTypes, formatPayload, choiceBtnClass, planDetailMethods } from './helper'
import { Input, FilePicker, SectionLegend, SelectionCard, StepShell, SelectField, CheckboxCard } from 'components/Profile';
import { blueCheckboxCardClass, blueCheckboxClass, choiceBtnSelected, choiceBtnUnselected } from 'components/Profile/helper';
import { connect } from 'react-redux';
import { ProfileActions } from 'store/actions';
import toast from 'react-hot-toast';
import { useOutletContext } from 'react-router-dom';

const enhancer = connect(null, { ...ProfileActions })

const Form = ({ updateInsurance, insurance = null, practice = null, onSaved = null, inModal = false, formId = '' }) => {
  const { sectionData, mergeSection, goToNextStep }: any = useOutletContext() || {};
  const initialData = insurance ?? sectionData.insurance;
  const { insurance_companies } = practice ?? sectionData.practice ?? {};
  const insuranceCompanies = insurance_companies?.data || [];
  const carrierOptions = insuranceCompanies.map((company) => ({
    value: company.id ?? '',
    label: company.attributes?.title ?? '',
    logo: company.attributes?.logo_url,
  }));

  const handleSubmit = async (values) => {
    try {
      const { data } = await updateInsurance(formatPayload(values))

      toast.success('Insurance data saved successfully');

      if (onSaved)
        onSaved(data);
      else {
        mergeSection('insurance', data);
        goToNextStep('insurance');
      }

    } catch (error) {
      toast.error(error.errors || 'Failed to save insurance data. Please try again later.');
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(initialData)}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <StepShell stepId="insurance" isSubmitting={isSubmitting} inModal={inModal} formId={formId}>
          <div className="flex items-center justify-between gap-3 mb-6 border border-gray-200 rounded-xl p-4">
            <div>
              <p className="text-md font-bold mb-2">Are you using dental insurance?</p>
              <p className="text-gray-500 mt-1 text-xs">Choose No if you're self-pay — we'll skip the rest.</p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setFieldValue('active', true)}
                className={choiceBtnClass(values.active)}
              >
                Yes
              </button>

              <button
                type="button"
                onClick={() => setFieldValue('active', false)}
                className={choiceBtnClass(!values.active)}
              >
                No
              </button>
            </div>
          </div>

          {values.active &&
            <div>
              <div className="mb-6 border border-gray-200 rounded-xl p-4">
                <p className="text-md font-bold mb-2">How would you like to give us your plan details?</p>
                <p className="text-gray-500 mt-1 text-xs mb-4">Pick whichever is easier, do both, or skip it for now — you can always add this later.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {planDetailMethods.map((method) => (
                    <CheckboxCard
                      key={method.name}
                      name={method.name}
                      label={method.label}
                      description={method.description}
                      className={blueCheckboxCardClass}
                      checkboxClassName={blueCheckboxClass}
                    />
                  ))}
                </div>
              </div>

              {values.enter_manually && <>
                <SectionLegend>Policy holder</SectionLegend>

                <SelectionCard
                  options={policyHolders}
                  selectedOption={values.policy_holder}
                  onPress={(_option) => setFieldValue('policy_holder', _option)}
                  selectedStyle={choiceBtnSelected}
                  unselectedStyle={choiceBtnUnselected}
                />

                <SectionLegend hint="All optional">Plan details</SectionLegend>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <SelectField
                    name="insurance_company_id"
                    label={"Insurance carrier"}
                    placeholder={"Select insurance carrier"}
                    options={carrierOptions}
                  />

                  <Input
                    name="plan_type"
                    label={"Plan type"}
                    placeholder={"Select plan"}
                    as="select"
                  >
                    <option value="" disabled>Select plan</option>
                    {insurancePlanTypes.map((_options, index) => (
                      <option key={index} value={_options.value}>{_options.label}</option>
                    ))}
                  </Input>

                  <Input
                    name="member_id"
                    label={"Member ID"}
                    placeholder={"Member / subscriber ID"}
                  />

                  <Input
                    name="group_number"
                    label={"Group number"}
                    placeholder={"Group #"}
                  />
                </div>
              </>}

              {values.upload_card && <>
                <SectionLegend hint="All optional">Insurance card</SectionLegend>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  <FilePicker
                    name='card_front_pic'
                    placeholder={"Add front of card"}
                  />

                  <FilePicker
                    name='card_back_pic'
                    placeholder={"Add back of card"}
                  />
                </div>
              </>}
            </div>
          }
        </StepShell>
      )}
    </Formik>
  )
}

export default enhancer(Form);
